import type { AutoCompleteResult } from '@awazevr/design-system.components.autocomplete'
import {
  DataLayerEventType,
  DataLayerFactory,
  type TmsContextType,
} from '@awazevr/mfe-common.contexts.tms'

import { OPTIMIZELY_EVENTS } from 'src/constants'
import { newrelicErrorHandler } from 'src/utils/errorHandler/newrelicErrorHandler'

import type { PlacesSuggestions, Suggestion } from './SearchForm.types'

type TrackOptimizelyEvent = (eventKey: string) => void

const fireLocationSuggestionOptimizelyEvent = (
  placesResult: Suggestion[],
  trackOptimizelyEvent?: TrackOptimizelyEvent,
): void => {
  if (!trackOptimizelyEvent) {
    return
  }

  const hasInternalResults =
    placesResult.length > 0 && !placesResult[0].type?.includes('GooglePlaces')

  trackOptimizelyEvent(
    hasInternalResults
      ? OPTIMIZELY_EVENTS.INTERNAL_LOCATION_SUGGESTION_SHOWN
      : OPTIMIZELY_EVENTS.INTERNAL_LOCATION_SUGGESTION_NO_RESULTS,
  )
}

type OriginalAutoSuggestSearch = (
  searchTerm: string,
  accommodationType: string,
  propertiesOnly: boolean,
) => Promise<AutoCompleteResult[]>

const fireAutoSuggestTrackingEvents = ({
  searchTerm,
  accommodationType,
  destinations,
  properties,
  noResult,
  tms,
}: {
  searchTerm: string
  accommodationType?: string
  destinations: string[]
  properties: string[]
  noResult: boolean
  tms?: TmsContextType
}): void => {
  if (!tms) {
    return
  }

  if (noResult) {
    tms.updateDataLayer('link', 'auto_suggest_noresult', {
      search_term: searchTerm,
    })
    return
  }

  new DataLayerFactory()
    .getEvent(DataLayerEventType.AutoSuggestImpression)
    .update(
      tms,
      {
        searchTerm,
        accommodationType,
        destinations,
        properties,
      },
      newrelicErrorHandler,
    )
}

export const combineAutoSuggestSearch = (
  getSuggestions: PlacesSuggestions,
  originalAutoSuggestSearch: OriginalAutoSuggestSearch,
  tms?: TmsContextType,
  trackOptimizelyEvent?: TrackOptimizelyEvent,
): ((
  searchTerm: string | null,
  accommodationType?: string,
) => Promise<AutoCompleteResult[]>) => {
  return async (
    searchTerm: string | null,
    accommodationType?: string,
  ): Promise<AutoCompleteResult[]> => {
    if (!searchTerm) {
      return []
    }

    const selectedAccommodationType = accommodationType ?? ''
    const forceLocationLookupIfBoats =
      selectedAccommodationType === 'boat-holidays'

    if (forceLocationLookupIfBoats) {
      const originalResult = await originalAutoSuggestSearch(
        searchTerm,
        selectedAccommodationType,
        false,
      )

      fireAutoSuggestTrackingEvents({
        searchTerm,
        accommodationType,
        destinations: originalResult
          .filter((result) => !result.property)
          .map(({ name }) => name),
        properties: originalResult
          .filter((result) => result.property)
          .map(({ name }) => name),
        noResult: originalResult.length === 0,
        tms,
      })

      return originalResult
    }

    const [placesResult, originalResult] = await Promise.all([
      getSuggestions(searchTerm),
      originalAutoSuggestSearch(searchTerm, selectedAccommodationType, true),
    ])

    const propertyResults = originalResult.filter((r) => r.property)

    fireAutoSuggestTrackingEvents({
      searchTerm,
      accommodationType,
      destinations: placesResult.map(({ name }) => name),
      properties: propertyResults.map(({ name }) => name),
      noResult: placesResult.length === 0 && propertyResults.length === 0,
      tms,
    })

    fireLocationSuggestionOptimizelyEvent(placesResult, trackOptimizelyEvent)

    return [...placesResult, ...propertyResults]
  }
}
